import { supabase, sessionManager } from './supabase';
import { tokenService } from './tokenService';

// Session timeout configuration
interface SessionTimeoutConfig {
  idleTimeout: number; // Time in milliseconds before logout due to inactivity
  maxSessionDuration: number; // Maximum session duration in milliseconds
  warningTime: number; // Time before logout to show warning
  checkInterval: number; // How often to check for timeout
}

// Default configuration
const DEFAULT_CONFIG: SessionTimeoutConfig = {
  idleTimeout: 30 * 60 * 1000, // 30 minutes
  maxSessionDuration: 8 * 60 * 60 * 1000, // 8 hours
  warningTime: 5 * 60 * 1000, // 5 minutes
  checkInterval: 60 * 1000, // 1 minute
};

// Event types for session timeout
export type SessionTimeoutEvent = 'warning' | 'timeout' | 'activity' | 'logout';

// Event listener type
export type SessionTimeoutListener = (event: SessionTimeoutEvent, data?: any) => void;

export class SessionTimeoutService {
  private static instance: SessionTimeoutService;
  private config: SessionTimeoutConfig;
  private lastActivity: number;
  private sessionStart: number;
  private timeoutInterval: NodeJS.Timeout | null = null;
  private warningShown: boolean = false;
  private listeners: SessionTimeoutListener[] = [];
  private isActive: boolean = false;

  private constructor(config: Partial<SessionTimeoutConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.lastActivity = Date.now();
    this.sessionStart = Date.now();
    this.setupActivityListeners();
  }

  public static getInstance(config?: Partial<SessionTimeoutConfig>): SessionTimeoutService {
    if (!SessionTimeoutService.instance) {
      SessionTimeoutService.instance = new SessionTimeoutService(config);
    }
    return SessionTimeoutService.instance;
  }

  /**
   * Starts the session timeout monitoring
   */
  public start(): void {
    if (this.isActive) {
      return;
    }

    this.isActive = true;
    this.lastActivity = Date.now();
    this.sessionStart = Date.now();
    this.warningShown = false;

    console.log('SessionTimeoutService: Starting session monitoring');
    
    this.timeoutInterval = setInterval(() => {
      this.checkTimeout();
    }, this.config.checkInterval);

    this.notifyListeners('activity', { started: true });
  }

  /**
   * Stops the session timeout monitoring
   */
  public stop(): void {
    if (!this.isActive) {
      return;
    }

    this.isActive = false;
    
    if (this.timeoutInterval) {
      clearInterval(this.timeoutInterval);
      this.timeoutInterval = null;
    }

    console.log('SessionTimeoutService: Stopped session monitoring');
  }

  /**
   * Resets the activity timer
   */
  public resetActivity(): void {
    if (!this.isActive) {
      return;
    }

    this.lastActivity = Date.now();
    this.warningShown = false;
    this.notifyListeners('activity', { timestamp: this.lastActivity });
  }

  /**
   * Checks for session timeout conditions
   */
  private async checkTimeout(): Promise<void> {
    if (!this.isActive) {
      return;
    }

    const now = Date.now();
    const idleTime = now - this.lastActivity;
    const sessionDuration = now - this.sessionStart;

    // Check if user is still authenticated
    const isAuthenticated = await tokenService.isAuthenticated();
    if (!isAuthenticated) {
      console.log('SessionTimeoutService: User no longer authenticated');
      this.handleLogout('authentication_lost');
      return;
    }

    // Check maximum session duration
    if (sessionDuration >= this.config.maxSessionDuration) {
      console.log('SessionTimeoutService: Maximum session duration exceeded');
      this.handleLogout('max_duration');
      return;
    }

    // Check idle timeout
    if (idleTime >= this.config.idleTimeout) {
      console.log('SessionTimeoutService: Idle timeout exceeded');
      this.handleLogout('idle_timeout');
      return;
    }

    // Check if warning should be shown
    const timeUntilIdleTimeout = this.config.idleTimeout - idleTime;
    const timeUntilMaxDuration = this.config.maxSessionDuration - sessionDuration;
    const timeUntilTimeout = Math.min(timeUntilIdleTimeout, timeUntilMaxDuration);

    if (timeUntilTimeout <= this.config.warningTime && !this.warningShown) {
      this.warningShown = true;
      const warningType = timeUntilIdleTimeout < timeUntilMaxDuration ? 'idle' : 'max_duration';
      
      console.log(`SessionTimeoutService: Showing ${warningType} warning`);
      this.notifyListeners('warning', {
        type: warningType,
        timeRemaining: timeUntilTimeout,
        timeRemainingMinutes: Math.ceil(timeUntilTimeout / 60000)
      });
    }
  }

  /**
   * Handles logout due to timeout
   */
  private async handleLogout(reason: string): Promise<void> {
    console.log(`SessionTimeoutService: Logging out due to ${reason}`);
    
    this.stop();
    this.notifyListeners('timeout', { reason });
    
    try {
      // Clear session and logout
      await sessionManager.clearSession();
      this.notifyListeners('logout', { reason });
    } catch (error) {
      console.error('SessionTimeoutService: Error during logout:', error);
    }
  }

  /**
   * Extends the session by resetting timers
   */
  public extendSession(): void {
    if (!this.isActive) {
      return;
    }

    console.log('SessionTimeoutService: Extending session');
    this.resetActivity();
    
    // Reset session start time if we're close to max duration
    const now = Date.now();
    const sessionDuration = now - this.sessionStart;
    const remainingTime = this.config.maxSessionDuration - sessionDuration;
    
    if (remainingTime < this.config.warningTime) {
      this.sessionStart = now;
      console.log('SessionTimeoutService: Reset session start time');
    }
  }

  /**
   * Gets the current session status
   */
  public getSessionStatus(): {
    isActive: boolean;
    idleTime: number;
    sessionDuration: number;
    timeUntilIdleTimeout: number;
    timeUntilMaxDuration: number;
  } {
    const now = Date.now();
    const idleTime = now - this.lastActivity;
    const sessionDuration = now - this.sessionStart;
    
    return {
      isActive: this.isActive,
      idleTime,
      sessionDuration,
      timeUntilIdleTimeout: Math.max(0, this.config.idleTimeout - idleTime),
      timeUntilMaxDuration: Math.max(0, this.config.maxSessionDuration - sessionDuration),
    };
  }

  /**
   * Updates the configuration
   */
  public updateConfig(newConfig: Partial<SessionTimeoutConfig>): void {
    this.config = { ...this.config, ...newConfig };
    console.log('SessionTimeoutService: Configuration updated', this.config);
  }

  /**
   * Adds an event listener
   */
  public addEventListener(listener: SessionTimeoutListener): void {
    this.listeners.push(listener);
  }

  /**
   * Removes an event listener
   */
  public removeEventListener(listener: SessionTimeoutListener): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  /**
   * Notifies all listeners of an event
   */
  private notifyListeners(event: SessionTimeoutEvent, data?: any): void {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('SessionTimeoutService: Error in event listener:', error);
      }
    });
  }

  /**
   * Sets up activity listeners for user interaction
   */
  private setupActivityListeners(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const activityHandler = () => {
      this.resetActivity();
    };

    // Throttle activity updates to avoid excessive calls
    let lastUpdate = 0;
    const throttledHandler = () => {
      const now = Date.now();
      if (now - lastUpdate > 1000) { // Update at most once per second
        lastUpdate = now;
        activityHandler();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, throttledHandler, true);
    });

    // Listen for visibility changes
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.resetActivity();
      }
    });

    // Listen for focus events
    window.addEventListener('focus', () => {
      this.resetActivity();
    });
  }
}

// Export singleton instance
export const sessionTimeoutService = SessionTimeoutService.getInstance();